/**
 * Search Filter Component
 * Reusable search input with debounce and keyboard shortcut support
 * Press "/" to focus search (Ctrl+K opens the global palette instead)
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  TextField,
  InputAdornment,
  IconButton,
  TextFieldProps,
  Tooltip,
  Typography,
  Box,
} from "@mui/material";
import { Search as SearchIcon, Clear as ClearIcon } from "@mui/icons-material";

export interface SearchFilterProps {
  /** Placeholder text */
  placeholder?: string;
  /** Current value */
  value?: string;
  /** Called when search value changes (after debounce) */
  onSearch: (value: string) => void;
  /** Debounce delay in ms */
  debounceMs?: number;
  /** Size of the input */
  size?: TextFieldProps["size"];
  /** Full width */
  fullWidth?: boolean;
  /** Minimum width */
  minWidth?: number | string;
  /** Whether to show clear button */
  showClear?: boolean;
  /** Aria label for accessibility */
  ariaLabel?: string;
  /** Enable the "/" focus shortcut */
  enableShortcut?: boolean;
  /** Show keyboard shortcut hint */
  showShortcutHint?: boolean;
}

export const SearchFilter: React.FC<SearchFilterProps> = ({
  placeholder = "Search...",
  value: externalValue,
  onSearch,
  debounceMs = 400,
  size = "small",
  fullWidth = false,
  minWidth = 200,
  showClear = true,
  ariaLabel = "Search",
  enableShortcut = true,
  showShortcutHint = true,
}) => {
  const [internalValue, setInternalValue] = useState(externalValue || "");
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync with external value
  useEffect(() => {
    if (externalValue !== undefined) {
      setInternalValue(externalValue);
    }
  }, [externalValue]);

  // Keep onSearch in a ref so the debounce effect can call the latest
  // callback without listing it as a dependency. If onSearch were a dep,
  // any parent re-render that re-creates the callback (e.g. on every URL
  // change when the parent uses useUrlFilters) would re-arm the debounce
  // timer and re-emit -- harmless for typing, but a hidden pagination
  // breaker for parents that reset page on search change.
  const onSearchRef = useRef(onSearch);
  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  // Only emit onSearch after the user has actually typed. Initial mount
  // and external-value syncs must not call onSearch -- otherwise the
  // parent's "search changed" handler runs unprompted and clobbers other
  // filter state (most commonly the page index, which list pages reset
  // to 1 on search change). Reproducer: e2e/list-pagination.spec.ts.
  const userTypedRef = useRef(false);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      userTypedRef.current = true;
      setInternalValue(e.target.value);
    },
    [],
  );

  // Debounced search -- depends only on the value, never on the callback.
  useEffect(() => {
    if (!userTypedRef.current) return;
    const timer = setTimeout(() => {
      onSearchRef.current(internalValue);
    }, debounceMs);
    return () => clearTimeout(timer);
  }, [internalValue, debounceMs]);

  /**
   * "/" focuses this page's filter.
   *
   * Ctrl+K belongs to the global palette and is handled once, in
   * KeyboardShortcutsProvider. Claiming it here as well meant both fired:
   * the palette opened AND this input was focused and its text selected
   * behind the modal, so dismissing the palette left the person's filter
   * selected and the next character they typed replaced it.
   */
  useEffect(() => {
    if (!enableShortcut) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      const isInput =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable;

      // "/" key (only when not in input)
      if (event.key === "/" && !isInput) {
        event.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }

      // Escape to blur search
      if (event.key === "Escape" && document.activeElement === inputRef.current) {
        inputRef.current?.blur();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [enableShortcut]);

  const handleClear = useCallback(() => {
    userTypedRef.current = true;
    setInternalValue("");
    onSearchRef.current("");
    inputRef.current?.focus();
  }, []);

  return (
    <TextField
      inputRef={inputRef}
      placeholder={placeholder}
      value={internalValue}
      onChange={handleChange}
      size={size}
      fullWidth={fullWidth}
      sx={{ minWidth, flex: fullWidth ? 1 : undefined }}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {showClear && internalValue ? (
                  <IconButton
                    size="small"
                    onClick={handleClear}
                    aria-label="Clear search"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                ) : enableShortcut && showShortcutHint && !internalValue ? (
                  <Tooltip title='Press "/" to search this page'>
                    <Typography
                      variant="caption"
                      sx={{
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 0.5,
                        bgcolor: "action.hover",
                        color: "text.secondary",
                        fontSize: "0.7rem",
                        fontFamily: "monospace",
                        cursor: "default",
                        display: { xs: "none", sm: "block" },
                      }}
                    >
                      /
                    </Typography>
                  </Tooltip>
                ) : null}
              </Box>
            </InputAdornment>
          ),
        },

        htmlInput: {
          "aria-label": ariaLabel,
        }
      }} />
  );
};

export default SearchFilter;
